import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './Button';
const articles = [{
  title: 'Building Product Sense',
  excerpt: 'How to develop the intuition that separates good product managers from great ones.',
  date: 'May 15, 2023'
}, {
  title: 'The Art and Science of CRO',
  excerpt: 'A data-driven approach to conversion rate optimization that actually works.',
  date: 'Mar 22, 2023'
}, {
  title: 'AI in Product Development',
  excerpt: 'Practical ways to leverage AI to enhance your product development process.',
  date: 'Jan 10, 2023'
}];
export function Writing() {
  const scrollToNewsletter = () => {
    const newsletterSection = document.getElementById('newsletter');
    if (newsletterSection) {
      newsletterSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section id="writing" className="py-28 px-6 md:px-12 relative overflow-hidden" style={{
    backgroundColor: '#F4F6ED'
  }} data-id="element-327">
      {/* Decorative elements */}
      <div className="absolute top-40 left-10 w-16 h-16 border border-dashed border-ink-300 rounded-full opacity-30" data-id="element-328"></div>
      <div className="absolute bottom-40 right-10 w-24 h-24 border border-dotted border-ink-300 rounded-full opacity-30" data-id="element-329"></div>
      <div className="container mx-auto max-w-5xl" data-id="element-330">
        <motion.div className="mb-20" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-331">
          <div className="relative inline-block" data-id="element-332">
            <h2 className="text-3xl font-currency italic tracking-wide mb-5 currency-heading" data-id="element-333">
              Writing
            </h2>
            <div className="absolute -right-8 top-1/2 transform -translate-y-1/2 w-6 h-px bg-ink-900" data-id="element-334"></div>
            <div className="absolute -left-8 top-1/2 transform -translate-y-1/2 w-6 h-px bg-ink-900" data-id="element-335"></div>
          </div>
          <div className="h-px w-16 bg-ink-900 mb-10" data-id="element-336"></div>
          <p className="text-ink-600 max-w-2xl font-serif" data-id="element-337">
            Thoughts on product management, CRO, and the impact of AI on product
            development.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-16 mb-20" data-id="element-338">
          {articles.map((article, index) => <motion.div key={index} className="relative" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} data-id="element-339">
              {/* Added decorative line elements */}
              <div className="absolute -top-3 -left-3 w-6 h-6" data-id="element-340">
                <div className="absolute top-0 left-0 w-6 h-px bg-ink-400" data-id="element-341"></div>
                <div className="absolute top-0 left-0 h-6 w-px bg-ink-400" data-id="element-342"></div>
              </div>
              <div className="text-ink-700 text-sm uppercase tracking-wider mb-3 font-mono blur-[2px] select-none opacity-70" data-id="element-343">
                {article.date}
              </div>
              <h3 className="text-xl font-currency tracking-wide mb-4 blur-[3px] select-none text-ink-700 opacity-70" data-id="element-344">
                {article.title}
              </h3>
              <div className="h-px w-12 bg-ink-200 mb-5" data-id="element-345"></div>
              <p className="text-ink-600 mb-5 font-serif blur-[4px] select-none opacity-60" data-id="element-346">
                {article.excerpt}
              </p>
              <div className="relative" data-id="element-347">
                <div className="absolute -inset-1 bg-parchment-50/30 backdrop-blur-sm rounded-sm" data-id="element-348"></div>
                <a href="#" className="text-ink-900 hover:text-ink-600 font-medium inline-flex items-center text-sm font-currency tracking-wide relative blur-[2px] select-none opacity-70" data-id="element-349">
                  Read more
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor" data-id="element-350">
                    <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-351" />
                  </svg>
                </a>
              </div>
            </motion.div>)}
        </div>
        <motion.div className="bg-white border border-ink-100 p-10 sm:p-14 relative" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-352">
          {/* Added decorative corner lines */}
          <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-ink-200" data-id="element-353"></div>
          <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-ink-200" data-id="element-354"></div>
          <div className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-ink-200" data-id="element-355"></div>
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-ink-200" data-id="element-356"></div>
          <div className="grid md:grid-cols-3 gap-10 items-center" data-id="element-357">
            <div className="md:col-span-2" data-id="element-358">
              <h3 className="text-2xl font-currency tracking-wide mb-4" data-id="element-359">
                Quick 3 Product Moves
              </h3>
              <p className="text-ink-600 mb-4 font-serif" data-id="element-360">
                A weekly newsletter with three actionable ideas to improve your
                product strategy, execution, and leadership.
              </p>
            </div>
            <div className="flex justify-center md:justify-end" data-id="element-361">
              <Button onClick={scrollToNewsletter} data-id="element-362">Subscribe</Button>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="max-w-4xl mx-auto mt-32 px-6" data-id="element-363">
        <div className="h-px bg-ink-200 w-full" data-id="element-364"></div>
      </div>
    </section>;
}